import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useLazyListEntitiesQuery } from '../apiSlice/entityApi';
import { useLazyListIndustriesQuery } from '../apiSlice/industryApi';
import { fetchAllPages } from '../apiSlice/useFetchAuxiliaryLists';
import {
  getDashboardErrorMessage,
  useGetDashboardAssignmentsQuery,
  useReplaceDashboardAssignmentsMutation
} from './dashboardApi';
import {
  DASHBOARD_ASSIGNMENT_SCOPE,
  DASHBOARD_STATUS
} from './dashboardContract';

const MAX_ASSIGNMENTS_PER_SCOPE = 500;
const HISTORY_PREVIEW_SIZE = 10;
const NOOP = () => {};

const sortedIds = (ids) => [...ids].sort();

const sameIds = (left, right) =>
  JSON.stringify(sortedIds(left)) === JSON.stringify(sortedIds(right));

const formatDate = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return 'Unknown date';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const optionMap = (options) =>
  new Map(options.map((option) => [option.id, option]));

const optionsForIds = (ids, options, unavailableLabel) => {
  const byId = optionMap(options);
  return ids.map(
    (id) =>
      byId.get(id) ?? {
        id,
        name: `${unavailableLabel} (${id})`,
        unavailable: true
      }
  );
};

const scopeName = (scopeType) =>
  scopeType === DASHBOARD_ASSIGNMENT_SCOPE.ENTITY ? 'Entity' : 'Industry type';

const DashboardAssignmentManager = ({
  activeAssignmentCount = 0,
  canManage,
  dashboardStatus,
  dashboardTemplateId,
  onSuccess = NOOP
}) => {
  const [entityIds, setEntityIds] = useState([]);
  const [industryTypeIds, setIndustryTypeIds] = useState([]);
  const [savedEntityIds, setSavedEntityIds] = useState([]);
  const [savedIndustryTypeIds, setSavedIndustryTypeIds] = useState([]);
  const [entities, setEntities] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [referencesLoading, setReferencesLoading] = useState(false);
  const [referencesError, setReferencesError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [loadEntities] = useLazyListEntitiesQuery();
  const [loadIndustries] = useLazyListIndustriesQuery();
  const assignmentsQuery = useGetDashboardAssignmentsQuery(
    dashboardTemplateId,
    { skip: !canManage || !dashboardTemplateId }
  );
  const [replaceAssignments, replaceState] =
    useReplaceDashboardAssignmentsMutation();
  const activeAssignments = assignmentsQuery.data?.activeAssignments ?? [];
  const assignmentHistory = assignmentsQuery.data?.assignmentHistory ?? [];
  const assignmentSignature = activeAssignments
    .map((assignment) => `${assignment.scopeType}:${assignment.scopeId}`)
    .sort()
    .join('|');
  const isPublished = dashboardStatus === DASHBOARD_STATUS.PUBLISHED;

  useEffect(() => {
    if (!canManage || !dashboardTemplateId) return undefined;
    let active = true;
    setReferencesLoading(true);
    setReferencesError(false);

    Promise.all([fetchAllPages(loadEntities), fetchAllPages(loadIndustries)])
      .then(([entityResult, industryResult]) => {
        if (!active) return;
        setEntities(entityResult.data ?? []);
        setIndustryTypes(industryResult.data ?? []);
        setReferencesError(Boolean(entityResult.error || industryResult.error));
        setReferencesLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setReferencesError(true);
        setReferencesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canManage, dashboardTemplateId, loadEntities, loadIndustries]);

  useEffect(() => {
    const assignmentKeys = assignmentSignature
      ? assignmentSignature.split('|')
      : [];
    const nextEntityIds = assignmentKeys
      .filter((key) => key.startsWith(`${DASHBOARD_ASSIGNMENT_SCOPE.ENTITY}:`))
      .map((key) => key.slice(DASHBOARD_ASSIGNMENT_SCOPE.ENTITY.length + 1));
    const nextIndustryTypeIds = assignmentKeys
      .filter((key) =>
        key.startsWith(`${DASHBOARD_ASSIGNMENT_SCOPE.INDUSTRY_TYPE}:`)
      )
      .map((key) =>
        key.slice(DASHBOARD_ASSIGNMENT_SCOPE.INDUSTRY_TYPE.length + 1)
      );
    setEntityIds(nextEntityIds);
    setIndustryTypeIds(nextIndustryTypeIds);
    setSavedEntityIds(nextEntityIds);
    setSavedIndustryTypeIds(nextIndustryTypeIds);
    setConfirmOpen(false);
    setShowAllHistory(false);
  }, [assignmentSignature, dashboardTemplateId]);

  const entityOptions = useMemo(
    () => optionsForIds(entityIds, entities, 'Unavailable entity'),
    [entities, entityIds]
  );
  const industryOptions = useMemo(
    () =>
      optionsForIds(
        industryTypeIds,
        industryTypes,
        'Unavailable industry type'
      ),
    [industryTypeIds, industryTypes]
  );
  const entityChoices = useMemo(
    () => [
      ...entities,
      ...entityOptions.filter(
        (option) => !entities.some((entity) => entity.id === option.id)
      )
    ],
    [entities, entityOptions]
  );
  const industryChoices = useMemo(
    () => [
      ...industryTypes,
      ...industryOptions.filter(
        (option) => !industryTypes.some((industry) => industry.id === option.id)
      )
    ],
    [industryOptions, industryTypes]
  );
  const dirty =
    !sameIds(entityIds, savedEntityIds) ||
    !sameIds(industryTypeIds, savedIndustryTypeIds);
  const scopeLabels = useMemo(
    () =>
      new Map(
        [...entities, ...industryTypes].map((item) => [item.id, item.name])
      ),
    [entities, industryTypes]
  );

  const saveAssignments = async () => {
    try {
      const result = await replaceAssignments({
        dashboardTemplateId,
        entityIds,
        industryTypeIds
      }).unwrap();
      setSavedEntityIds(entityIds);
      setSavedIndustryTypeIds(industryTypeIds);
      setConfirmOpen(false);
      const changedLocationCount = result.changedLocationIds?.length ?? 0;
      onSuccess(
        `Dashboard assignments saved. ${changedLocationCount} ${
          changedLocationCount === 1 ? 'location changed' : 'locations changed'
        } effective dashboard.`
      );
    } catch (_error) {
      // RTK Query exposes the normalized error through replaceState below.
    }
  };

  if (!canManage) {
    return (
      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', mt: 4, p: 3 }}
      >
        <Typography component="h2" fontSize={22} fontWeight={700}>
          Dashboard assignments
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          Only a customer administrator can view assignment history or assign a
          dashboard to entities and industry types.
        </Alert>
      </Paper>
    );
  }

  const displayedHistory = showAllHistory
    ? assignmentHistory
    : assignmentHistory.slice(0, HISTORY_PREVIEW_SIZE);
  const displayedAssignmentCount =
    assignmentsQuery.data?.activeAssignmentCount ?? activeAssignmentCount;
  const effectiveLocationCount =
    assignmentsQuery.data?.effectiveLocationCount ?? 0;

  return (
    <Paper
      data-testid="dashboard-assignment-manager"
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', mt: 4, p: 3 }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography component="h2" fontSize={22} fontWeight={700}>
            Dashboard assignments
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Choose where this published dashboard applies. Entity assignments
            take priority over industry-type assignments. Locations without any
            matching published assignment use the system default.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip
            label={`${displayedAssignmentCount} active ${
              displayedAssignmentCount === 1 ? 'assignment' : 'assignments'
            }`}
            size="small"
            variant="outlined"
          />
          <Chip
            color={effectiveLocationCount ? 'success' : 'default'}
            label={`${effectiveLocationCount} effective ${
              effectiveLocationCount === 1 ? 'location' : 'locations'
            }`}
            size="small"
            variant="outlined"
          />
        </Stack>
      </Stack>

      {(assignmentsQuery.isFetching || referencesLoading) && (
        <LinearProgress sx={{ mt: 2 }} />
      )}
      {assignmentsQuery.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(assignmentsQuery.error)}
        </Alert>
      )}
      {referencesError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Entity and industry-type options could not be loaded. Existing
          assignments have not been changed.
        </Alert>
      )}
      {!isPublished && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Publish before assigning</AlertTitle>
          Saved assignment history is retained, but assignments can only be
          changed while this dashboard is published.
        </Alert>
      )}

      <Stack spacing={2.5} sx={{ mt: 3 }}>
        <Autocomplete
          disableCloseOnSelect
          disabled={
            !isPublished ||
            Boolean(assignmentsQuery.error) ||
            assignmentsQuery.isLoading ||
            referencesLoading ||
            referencesError
          }
          getOptionDisabled={(option) =>
            entityIds.length >= MAX_ASSIGNMENTS_PER_SCOPE &&
            !entityIds.includes(option.id)
          }
          getOptionLabel={(option) => option.name || option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          onChange={(_event, value) =>
            setEntityIds(value.map((item) => item.id))
          }
          options={entityChoices}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={`${entityIds.length}/${MAX_ASSIGNMENTS_PER_SCOPE} selected. Overrides an industry-type assignment for the same location.`}
              label="Entities"
              placeholder="Search entities"
            />
          )}
          value={entityOptions}
        />
        <Autocomplete
          disableCloseOnSelect
          disabled={
            !isPublished ||
            Boolean(assignmentsQuery.error) ||
            assignmentsQuery.isLoading ||
            referencesLoading ||
            referencesError
          }
          getOptionDisabled={(option) =>
            industryTypeIds.length >= MAX_ASSIGNMENTS_PER_SCOPE &&
            !industryTypeIds.includes(option.id)
          }
          getOptionLabel={(option) => option.name || option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          onChange={(_event, value) =>
            setIndustryTypeIds(value.map((item) => item.id))
          }
          options={industryChoices}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={`${industryTypeIds.length}/${MAX_ASSIGNMENTS_PER_SCOPE} selected. Applies to locations without an entity override.`}
              label="Industry types"
              placeholder="Search industry types"
            />
          )}
          value={industryOptions}
        />
      </Stack>

      {replaceState.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(replaceState.error)}
        </Alert>
      )}
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.5 }}>
        <Button
          disabled={
            !dirty ||
            !isPublished ||
            Boolean(assignmentsQuery.error) ||
            referencesError ||
            assignmentsQuery.isLoading ||
            replaceState.isLoading
          }
          onClick={() => {
            replaceState.reset?.();
            setConfirmOpen(true);
          }}
          variant="contained"
        >
          Save assignments
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />
      <Typography component="h3" fontSize={18} fontWeight={700}>
        Assignment history
      </Typography>
      {assignmentHistory.length ? (
        <>
          <List dense disablePadding sx={{ mt: 1 }}>
            {displayedHistory.map((assignment) => (
              <ListItem
                disableGutters
                key={assignment.dashboardTemplateAssignmentId}
              >
                <ListItemText
                  primary={`${scopeName(assignment.scopeType)} · ${
                    scopeLabels.get(assignment.scopeId) ?? assignment.scopeId
                  }`}
                  secondary={`Assigned ${formatDate(
                    assignment.assignedAt
                  )} · Unassigned ${formatDate(assignment.unassignedAt)}`}
                />
              </ListItem>
            ))}
          </List>
          {assignmentHistory.length > HISTORY_PREVIEW_SIZE && (
            <Button
              onClick={() => setShowAllHistory((current) => !current)}
              size="small"
              sx={{ mt: 1 }}
            >
              {showAllHistory ? 'Show recent history' : 'Show all history'}
            </Button>
          )}
        </>
      ) : (
        <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
          No assignments have been removed from this dashboard.
        </Typography>
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => !replaceState.isLoading && setConfirmOpen(false)}
        open={confirmOpen}
      >
        <DialogTitle>Replace dashboard assignments?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will set the complete assignment list to {entityIds.length}{' '}
            {entityIds.length === 1 ? 'entity' : 'entities'} and{' '}
            {industryTypeIds.length}{' '}
            {industryTypeIds.length === 1 ? 'industry type' : 'industry types'}.
            A selected scope already assigned to another dashboard will move
            here. Removed and moved assignments remain in history.
          </DialogContentText>
          {replaceState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(replaceState.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={replaceState.isLoading}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={replaceState.isLoading}
            onClick={saveAssignments}
            variant="contained"
          >
            {replaceState.isLoading ? 'Saving…' : 'Confirm assignments'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DashboardAssignmentManager;
